
import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Eye,
  Check,
  Flag,
  Star,
  MessageSquare,
  Trash2,
  X,
} from 'lucide-react';
import { formatDate } from '@/utils/dateFormat';
import { toast } from '@/components/ui/Toast';
import Pagination from '@/components/ui/Pagination';
import { TableSkeleton } from '@/components/ui/SkeletonLoader';
import SimpleSelect from '@/components/ui/SimpleSelect';
import SearchInputDebounced from '@/components/ui/SearchInputDebounced';
import ActionMenu from '@/components/ui/ActionMenu';
import DeleteConfirmationModal from '@/components/ui/DeleteConfirmationModal';
import TooltipArrow from '@/components/ui/TooltipArrow';
import StarRating from '../StarRating';
import AdminResponseModal from './AdminResponseModal';
import { reviewService } from '@/services/review.service';
import { ReviewStatus, ReviewType } from '@/types/review.types';
import type { Review, AdminReviewListParams } from '@/types/review.types';

// Filter options
const REVIEW_TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: ReviewType.COURSE, label: 'Course' },
  { value: ReviewType.CERTIFICATE, label: 'Certificate' },
  { value: ReviewType.LEARNING_PATH, label: 'Learning Path' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: ReviewStatus.APPROVED, label: 'Approved' },
  { value: ReviewStatus.FLAGGED, label: 'Flagged' },
  { value: ReviewStatus.DELETED, label: 'Deleted' },
];

const RATING_OPTIONS = [
  { value: '', label: 'All Ratings' },
  { value: '5', label: '5 Stars' },
  { value: '4', label: '4 Stars' },
  { value: '3', label: '3 Stars' },
  { value: '2', label: '2 Stars' },
  { value: '1', label: '1 Star' },
];

const STATUS_CONFIG: Record<ReviewStatus, { bg: string; text: string; label: string }> = {
  [ReviewStatus.APPROVED]: { bg: 'bg-green-50', text: 'text-green-700', label: 'Approved' },
  [ReviewStatus.FLAGGED]: { bg: 'bg-orange-50', text: 'text-orange-700', label: 'Flagged' },
  [ReviewStatus.DELETED]: { bg: 'bg-red-50', text: 'text-red-700', label: 'Deleted' },
};

const TYPE_LABELS: Record<ReviewType, string> = {
  [ReviewType.COURSE]: 'Course',
  [ReviewType.CERTIFICATE]: 'Certificate',
  [ReviewType.LEARNING_PATH]: 'Learning Path',
};

const DEFAULT_PAGE_SIZE = 10;

export interface ReviewFilters {
  review_type?: ReviewType | '';
  status?: ReviewStatus | '';
  rating?: string;
  search?: string;
}

interface ReviewsManagementTableProps {
  onFiltersChange?: (filters: ReviewFilters) => void;
  canWrite?: boolean;
}

export default function ReviewsManagementTable({ onFiltersChange, canWrite = true }: ReviewsManagementTableProps) {
  const [params, setParams] = useState<AdminReviewListParams>({
    skip: 0,
    limit: DEFAULT_PAGE_SIZE,
  });
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [searchQuery, setSearchQuery] = useState('');
  const [reviewTypeFilter, setReviewTypeFilter] = useState<ReviewType | ''>('');
  const [statusFilter, setStatusFilter] = useState<ReviewStatus | ''>('');
  const [ratingFilter, setRatingFilter] = useState<string>('');
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [reviewToDelete, setReviewToDelete] = useState<Review | null>(null);

  // Notify parent of filter changes
  const notifyFiltersChange = (newFilters: Partial<ReviewFilters>) => {
    const filters: ReviewFilters = {
      review_type: newFilters.review_type ?? reviewTypeFilter,
      status: newFilters.status ?? statusFilter,
      rating: newFilters.rating ?? ratingFilter,
      search: newFilters.search ?? searchQuery,
    };
    onFiltersChange?.(filters);
  };

  // Fetch reviews using direct service call
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['reviews', 'admin', params],
    queryFn: () => reviewService.getAdminReviews(params),
  });

  // Mutations
  const updateStatus = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { status: ReviewStatus } }) =>
      reviewService.updateReviewStatus(id, data),
    onSuccess: () => {
      toast.success('Review status updated');
      refetch();
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update status');
    },
  });

  const toggleFeatured = useMutation({
    mutationFn: ({ id, is_featured }: { id: string; is_featured: boolean }) =>
      reviewService.toggleFeatured(id, { is_featured }),
    onSuccess: () => {
      toast.success('Featured status updated');
      refetch();
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update featured status');
    },
  });

  const deleteReview = useMutation({
    mutationFn: (id: string) => reviewService.adminDeleteReview(id),
    onSuccess: () => {
      toast.success('Review deleted');
      refetch();
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete review');
    },
  });

  // Calculate pagination
  const reviews = data?.reviews || [];
  const totalCount = data?.total_count || 0;
  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const currentPage = Math.floor((params.skip || 0) / itemsPerPage) + 1;

  // Filter handlers
  const handleReviewTypeFilter = (type: ReviewType | '') => {
    setReviewTypeFilter(type);
    setParams((prev) => ({
      ...prev,
      review_type: type || undefined,
      skip: 0,
    }));
    notifyFiltersChange({ review_type: type });
  };

  const handleStatusFilter = (status: ReviewStatus | '') => {
    setStatusFilter(status);
    setParams((prev) => ({
      ...prev,
      status: status || undefined,
      skip: 0,
    }));
    notifyFiltersChange({ status });
  };

  const handleRatingFilter = (rating: string) => {
    setRatingFilter(rating);
    setParams((prev) => ({
      ...prev,
      rating: rating ? parseInt(rating) : undefined,
      skip: 0,
    }));
    notifyFiltersChange({ rating });
  };

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    setParams((prev) => ({
      ...prev,
      search: value || undefined,
      skip: 0,
    }));
    notifyFiltersChange({ search: value });
  };

  const handlePageChange = (page: number) => {
    setParams((prev) => ({ ...prev, skip: (page - 1) * itemsPerPage }));
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setParams((prev) => ({ ...prev, limit: newItemsPerPage, skip: 0 }));
  };

  const handleApprove = async (review: Review) => {
    await updateStatus.mutateAsync({
      id: review._id,
      data: { status: ReviewStatus.APPROVED },
    });
  };

  const handleFlag = async (review: Review) => {
    await updateStatus.mutateAsync({
      id: review._id,
      data: { status: ReviewStatus.FLAGGED },
    });
  };

  const handleToggleFeatured = async (review: Review) => {
    await toggleFeatured.mutateAsync({
      id: review._id,
      is_featured: !review.is_featured,
    });
  };

  const handleDeleteClick = (review: Review) => {
    setReviewToDelete(review);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (reviewToDelete) {
      await deleteReview.mutateAsync(reviewToDelete._id);
      setShowDeleteModal(false);
      setReviewToDelete(null);
    }
  };

  const handleOpenResponseModal = (review: Review) => {
    setSelectedReview(review);
    setShowResponseModal(true);
  };

  // Build action menu items for a review
  const getActionMenuItems = (review: Review) => [
    {
      label: 'View Details',
      icon: <Eye size={14} />,
      onClick: () => setSelectedReview(review),
    },
    {
      label: review.admin_response ? 'Edit Response' : 'Add Response',
      icon: <MessageSquare size={14} />,
      onClick: () => canWrite && handleOpenResponseModal(review),
      disabled: !canWrite,
    },
    {
      label: 'Delete',
      icon: <Trash2 size={14} />,
      onClick: () => canWrite && handleDeleteClick(review),
      variant: 'danger' as const,
      divider: true,
      disabled: !canWrite,
    },
  ];

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header with Search & Filters */}
      <div className="px-5 py-4 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          {/* Search Box - Left Side */}
          <div className="w-full sm:w-80">
            <SearchInputDebounced
              value={searchQuery}
              onChange={handleSearch}
              placeholder="Search by name, email, emp ID..."
              debounceDelay={400}
            />
          </div>

          {/* Filters - Right Side */}
          <div className="flex items-center gap-3">
            {/* Review Type Filter */}
            <div className="w-40">
              <SimpleSelect
                value={reviewTypeFilter}
                onChange={(val) => handleReviewTypeFilter((val as ReviewType) || '')}
                options={REVIEW_TYPE_OPTIONS}
                placeholder="All Types"
                isSearchable={false}
              />
            </div>

            {/* Status Filter */}
            <div className="w-36">
              <SimpleSelect
                value={statusFilter}
                onChange={(val) => handleStatusFilter((val as ReviewStatus) || '')}
                options={STATUS_OPTIONS}
                placeholder="All Status"
                isSearchable={false}
              />
            </div>

            {/* Rating Filter */}
            <div className="w-36">
              <SimpleSelect
                value={ratingFilter}
                onChange={(val) => handleRatingFilter(val?.toString() || '')}
                options={RATING_OPTIONS}
                placeholder="All Ratings"
                isSearchable={false}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <TableSkeleton rows={5} columns={7} showHeader />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Target
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rating
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Featured
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {reviews.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                  No reviews found
                </td>
              </tr>
            ) : (
              reviews.map((review) => (
                <tr key={review._id} className="hover:bg-gray-50">
                  {/* User */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-medium text-primary-700">
                          {review.user_details?.initials ||
                            `${review.user_details?.first_name?.charAt(0) || '?'}${review.user_details?.last_name?.charAt(0) || '?'}`}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {review.user_details?.first_name || 'Unknown'} {review.user_details?.last_name || 'User'}
                          {review.user_details?.empl_id && (
                            <span className="text-gray-500 font-normal"> ({review.user_details.empl_id})</span>
                          )}
                        </p>
                        <p className="text-xs text-gray-500 truncate">{review.user_details?.email || 'No email'}</p>
                      </div>
                    </div>
                  </td>

                  {/* Target */}
                  <td className="px-4 py-3">
                    <div className="w-[220px]">
                      <TooltipArrow content={review.entity_name || ''} position="bottom" delay={200} block>
                        <p className="text-sm text-gray-900 truncate cursor-default">
                          {review.entity_name || 'N/A'}
                        </p>
                      </TooltipArrow>
                      <p className="text-xs text-gray-500 capitalize">{TYPE_LABELS[review.review_type] || review.review_type}</p>
                    </div>
                  </td>

                  {/* Rating */}
                  <td className="px-4 py-3">
                    <StarRating value={review.rating} readonly size="sm" />
                  </td>

                  {/* Status */}
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full ${
                        STATUS_CONFIG[review.status]?.bg || 'bg-gray-100'
                      } ${STATUS_CONFIG[review.status]?.text || 'text-gray-600'}`}
                    >
                      {STATUS_CONFIG[review.status]?.label || review.status}
                    </span>
                  </td>

                  {/* Featured */}
                  <td className="px-4 py-3">
                    <button
                      onClick={() => canWrite && handleToggleFeatured(review)}
                      disabled={!canWrite}
                      className={`p-1.5 rounded-lg transition-colors ${
                        review.is_featured
                          ? 'bg-yellow-100 text-yellow-600'
                          : 'bg-gray-100 text-gray-400 hover:text-yellow-600'
                      }${!canWrite ? ' opacity-50 cursor-not-allowed' : ''}`}
                      title={!canWrite ? 'You do not have permission' : review.is_featured ? 'Remove from featured' : 'Add to featured'}
                    >
                      <Star size={16} className={review.is_featured ? 'fill-yellow-400' : ''} />
                    </button>
                  </td>

                  {/* Date */}
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-500">
                      {formatDate(review.createdAt, 'dd MMM yyyy')}
                    </span>
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {/* Quick Actions */}
                      {review.status === ReviewStatus.FLAGGED && (
                        <button
                          onClick={() => canWrite && handleApprove(review)}
                          disabled={!canWrite}
                          className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title={!canWrite ? 'You do not have permission' : 'Approve'}
                        >
                          <Check size={16} />
                        </button>
                      )}
                      {review.status === ReviewStatus.APPROVED && (
                        <button
                          onClick={() => canWrite && handleFlag(review)}
                          disabled={!canWrite}
                          className="p-1.5 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title={!canWrite ? 'You do not have permission' : 'Flag'}
                        >
                          <Flag size={16} />
                        </button>
                      )}

                      {/* More Menu */}
                      <ActionMenu items={getActionMenuItems(review)} align="right" />
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      )}

      {/* Pagination */}
      {totalCount > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalCount}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          showItemsPerPage
        />
      )}

      {/* Review Detail Modal */}
      {selectedReview && !showResponseModal && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-[100]"
            onClick={() => setSelectedReview(null)}
          />
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden">
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                <h3 className="font-semibold text-gray-900">Review Details</h3>
                <button
                  onClick={() => setSelectedReview(null)}
                  className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X size={20} className="text-gray-600" />
                </button>
              </div>
              <div className="px-5 py-4 max-h-[60vh] overflow-y-auto">
                {/* User Info */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
                    <span className="text-sm font-medium text-primary-700">
                      {selectedReview.user_details?.initials ||
                        `${selectedReview.user_details?.first_name?.charAt(0) || '?'}${selectedReview.user_details?.last_name?.charAt(0) || '?'}`}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {selectedReview.user_details?.first_name || 'Unknown'} {selectedReview.user_details?.last_name || 'User'}
                      {selectedReview.user_details?.empl_id && (
                        <span className="text-gray-500 font-normal"> ({selectedReview.user_details.empl_id})</span>
                      )}
                    </p>
                    <p className="text-sm text-gray-500">{selectedReview.user_details?.email || 'No email'}</p>
                  </div>
                </div>

                {/* Rating */}
                <div className="mb-4">
                  <StarRating value={selectedReview.rating} readonly size="md" showLabel />
                </div>

                {/* Target */}
                <div className="mb-4">
                  <p className="text-xs text-gray-500 mb-1">Target</p>
                  <TooltipArrow content={selectedReview.entity_name || ''} position="top" delay={200}>
                    <p className="font-medium text-gray-900 truncate cursor-default">{selectedReview.entity_name || 'N/A'}</p>
                  </TooltipArrow>
                  <p className="text-sm text-gray-500 capitalize">{TYPE_LABELS[selectedReview.review_type] || selectedReview.review_type}</p>
                </div>

                {/* Review Text */}
                <div className="mb-4">
                  <p className="text-xs text-gray-500 mb-1">Review</p>
                  <p className="text-gray-700 whitespace-pre-wrap">{selectedReview.review_text}</p>
                </div>

                {/* Admin Response */}
                {selectedReview.admin_response && (
                  <div className="p-3 bg-primary-50 rounded-lg mb-4">
                    <p className="text-xs text-primary-600 font-medium mb-1">Admin Response</p>
                    <p className="text-sm text-gray-700">{selectedReview.admin_response.text}</p>
                  </div>
                )}

                {/* Stats */}
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <span>Helpful: {selectedReview.helpful_count}</span>
                  <span>Reports: {selectedReview.report_count}</span>
                </div>
              </div>
              <div className="px-5 py-4 bg-gray-50 border-t border-gray-100 flex gap-3">
                <button
                  onClick={() => setSelectedReview(null)}
                  className="flex-1 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    if (!canWrite) return;
                    setShowResponseModal(true);
                  }}
                  disabled={!canWrite}
                  title={!canWrite ? 'You do not have permission' : undefined}
                  className={`flex-1 py-2.5 text-sm font-medium rounded-xl transition-colors ${
                    canWrite ? 'bg-gray-900 text-white hover:bg-gray-800' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {selectedReview.admin_response ? 'Edit Response' : 'Add Response'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Admin Response Modal */}
      <AdminResponseModal
        isOpen={showResponseModal}
        onClose={() => {
          setShowResponseModal(false);
          setSelectedReview(null);
        }}
        review={selectedReview}
        onSuccess={() => refetch()}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setReviewToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Review"
        message="Are you sure you want to delete this review? This action cannot be undone."
        isLoading={deleteReview.isPending}
      />
    </div>
  );
}
